import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { GitBranch, AlertTriangle, RefreshCw, Trash2, Lock, CheckCircle2, ArrowUpRight, Loader2 } from 'lucide-react';

const BUILDER_LABEL = { reroof: 'Re-Roof', painting: 'Re-Coat', guttering: 'Guttering', repairs: 'Repairs', maintenance: 'Maintenance' };
const STEPS = ['Builder', 'Components check', 'Quote Calc', 'Contractors / Materials', 'Save Quote PDF', 'Send'];

function Chip({ tone, icon: Icon, children, testId }) {
  const tones = { red: 'bg-rose-50 text-rose-800 border-rose-200', amber: 'bg-amber-50 text-amber-800 border-amber-200', green: 'bg-emerald-50 text-emerald-800 border-emerald-200', slate: 'bg-slate-50 text-slate-700 border-slate-200' };
  return <span data-testid={testId} className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${tones[tone]}`}><Icon size={11} />{children}</span>;
}

// Child quote safety banner — shows on every tab of a child appraisal.
export function ChildQuoteBanner({ api, appraisal, refreshKey, onChanged }) {
  const navigate = useNavigate();
  const [pf, setPf] = useState(null);
  const [busy, setBusy] = useState('');
  const id = appraisal?._id;

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/appraisals/${id}/child-preflight`); setPf(data); } catch { setPf(null); }
  }, [api, id]);
  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => {
    window.addEventListener('rtr:child-quote-refresh', load);
    return () => window.removeEventListener('rtr:child-quote-refresh', load);
  }, [load]);

  const act = async (key, fn, ok, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusy(key);
    try { const r = await fn(); toast.success(ok(r)); await load(); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Action failed'); }
    setBusy('');
  };

  if (!pf?.is_child) return null;
  const builder = pf.kind === 'builder';
  const stale = pf.diagram_stale || (pf.condition_diffs || []).length > 0;
  return (
    <div data-testid="child-quote-banner" className="rounded-md border-2 border-indigo-200 bg-indigo-50/50 px-4 py-3 space-y-2">
      <div className="flex flex-wrap items-center gap-2 text-sm text-indigo-950">
        <GitBranch size={16} className="text-indigo-700" />
        <b data-testid="child-quote-banner-title">{pf.appraisal_no ? `A${pf.appraisal_no}` : 'Draft'} · {builder ? `${BUILDER_LABEL[pf.builder_origin] || pf.builder_origin} builder quote` : `${pf.quote_type_ref || 'Variant'} (Runner variant)`}</b>
        <span className="text-indigo-800/80">child of {pf.parent_no ? `A${pf.parent_no}` : 'parent'}</span>
        <button type="button" data-testid="child-quote-open-parent" onClick={() => navigate(`/appraisals/${pf.parent_id}`)}
          className="inline-flex items-center gap-0.5 text-xs font-semibold text-indigo-700 hover:text-indigo-900">Open parent <ArrowUpRight size={12} /></button>
        <span className="flex-1" />
        {pf.quote_sent && <Chip tone="slate" icon={Lock} testId="child-chip-sent">Quote sent</Chip>}
        {pf.foreign_lines.length > 0 && <Chip tone="red" icon={AlertTriangle} testId="child-chip-foreign">{pf.foreign_lines.length} foreign line(s)</Chip>}
        {!pf.parity.ok && <Chip tone="red" icon={AlertTriangle} testId="child-chip-parity">Price check failed</Chip>}
        {builder && stale && <Chip tone="amber" icon={AlertTriangle} testId="child-chip-stale">Out of date vs parent</Chip>}
        {pf.changed_since_pdf && <Chip tone="amber" icon={AlertTriangle} testId="child-chip-changed-since-pdf">Changed since last PDF</Chip>}
        {pf.manual_lines.length > 0 && <Chip tone="slate" icon={CheckCircle2} testId="child-chip-manual">{pf.manual_lines.length} manual line(s)</Chip>}
        {pf.last_pdf && !pf.changed_since_pdf && <Chip tone="green" icon={CheckCircle2} testId="child-chip-pdf-ok">PDF up to date</Chip>}
      </div>
      {builder && (
        <p className="text-[11px] text-indigo-900/80" data-testid="child-quote-rules">
          Rules: edit builder lines in the <b>Builder</b> tab (they're locked on Components) · add extras as manual lines · edit the diagram / roof notes on the <b>parent</b>, then re-copy · Roof Details are shared with the whole property.
          <span className="ml-1 text-indigo-700">Steps: {STEPS.join(' → ')}</span>
        </p>
      )}
      {builder && (pf.foreign_lines.length > 0 || stale) && (
        <div className="flex flex-wrap gap-2">
          {pf.foreign_lines.length > 0 && (
            <button type="button" data-testid="child-remove-foreign-btn" disabled={!!busy}
              onClick={() => act('foreign', () => api.post(`/appraisals/${id}/remove-foreign-lines`), (r) => `Removed ${r.data.removed} foreign line(s)`,
                `Remove these lines that don't belong to this quote?\n\n${pf.foreign_lines.map(l => `• ${l.component_type} ${l.type_name} (${l.import_source})`).join('\n')}`)}
              className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50">
              {busy === 'foreign' ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />} Remove foreign lines
            </button>
          )}
          {stale && !pf.quote_sent && (
            <button type="button" data-testid="child-recopy-btn" disabled={!!busy}
              onClick={() => act('recopy', () => api.post(`/appraisals/${id}/recopy-from-parent`), (r) => `Re-copied ${r.data.pins} pins + roof notes from parent`,
                "Replace this quote's diagram pins and roof-condition notes with the parent's current versions? Then re-check the builder and Save Quote PDF.")}
              className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded border border-amber-400 bg-white text-amber-800 hover:bg-amber-50 disabled:opacity-50">
              {busy === 'recopy' ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Re-copy from parent
            </button>
          )}
        </div>
      )}
    </div>
  );
}
